import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, Pencil, Plus, Ticket, Trash2 } from 'lucide-react';
import type { CustomerCouponToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { FormField } from '~/components/system/form-field';
import { serverMessage } from '~/lib/api/server-error';
import { useCustomerCoupons, useCustomerCouponMutations } from '~/hooks/use-customer-subviews';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	customerId: number | null;
	customerName?: string;
}

const EMPTY: CustomerCouponToken = { cid: 0, cd: '', nm: '', bc: '', desc: '', amt: null, dsc: null, active: true };

const dateInput = (v: unknown) => (v ? String(v).slice(0, 10) : '');

/** Per-customer coupons sub-grid (legacy CustomerCoupons popup). List + inline add/edit. */
export function CustomerCouponsDialog({ open, onOpenChange, customerId, customerName }: Props) {
	const { t } = useTranslation();
	const { data, isLoading } = useCustomerCoupons(open ? customerId : null);
	const { create, update, remove } = useCustomerCouponMutations(customerId ?? 0);
	const [editing, setEditing] = useState<CustomerCouponToken | null>(null);

	useEffect(() => {
		if (!open) setEditing(null);
	}, [open]);

	const save = async () => {
		if (!editing) return;
		if (!editing.cd?.trim() || !editing.nm?.trim()) {
			toast.error(t('validation.required'));
			return;
		}
		try {
			if (editing.id) await update.mutateAsync(editing);
			else await create.mutateAsync(editing);
			toast.success(t('common.save'));
			setEditing(null);
		} catch (err) {
			toast.error(serverMessage(err));
		}
	};

	const del = (c: CustomerCouponToken) => {
		if (!c.id) return;
		remove.mutate(c.id, { onSuccess: () => toast.success(t('common.delete')), onError: e => toast.error(serverMessage(e)) });
	};

	const set = (patch: Partial<CustomerCouponToken>) => setEditing(prev => ({ ...(prev ?? EMPTY), ...patch }));
	const num = (v: string) => (v === '' ? null : Number(v));

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-[95vw] sm:max-w-3xl">
				<DialogHeader>
					<DialogTitle>{t('customers.list.coupons.title')}</DialogTitle>
					<DialogDescription>{customerName || t('customers.list.coupons.subtitle')}</DialogDescription>
				</DialogHeader>

				{editing ? (
					<div className="flex flex-col gap-3">
						<div className="grid grid-cols-2 gap-3">
							<FormField label={t('customers.list.coupons.code')}>
								<Input value={editing.cd ?? ''} onChange={e => set({ cd: e.target.value })} autoFocus />
							</FormField>
							<FormField label={t('customers.list.coupons.name')}>
								<Input value={editing.nm ?? ''} onChange={e => set({ nm: e.target.value })} />
							</FormField>
							<FormField label={t('customers.list.coupons.barcode')}>
								<Input value={editing.bc ?? ''} onChange={e => set({ bc: e.target.value })} />
							</FormField>
							<FormField label={t('customers.list.coupons.amount')}>
								<Input type="number" step="any" value={editing.amt ?? ''} onChange={e => set({ amt: num(e.target.value) })} />
							</FormField>
							<FormField label={t('customers.list.coupons.discount')}>
								<Input type="number" step="any" value={editing.dsc ?? ''} onChange={e => set({ dsc: num(e.target.value) })} />
							</FormField>
							<FormField label={t('customers.list.coupons.registerDate')}>
								<Input type="date" value={dateInput(editing.sdt)} onChange={e => set({ sdt: e.target.value || null })} />
							</FormField>
							<FormField label={t('customers.list.coupons.expiryDate')}>
								<Input type="date" value={dateInput(editing.edt)} onChange={e => set({ edt: e.target.value || null })} />
							</FormField>
						</div>
						<FormField label={t('customers.list.coupons.description')}>
							<Input value={editing.desc ?? ''} onChange={e => set({ desc: e.target.value })} />
						</FormField>
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="cp-active">{t('customers.list.coupons.active')}</Label>
							<Switch id="cp-active" checked={Boolean(editing.active)} onCheckedChange={next => set({ active: next })} />
						</div>
						<div className="flex justify-between">
							<Button variant="ghost" onClick={() => setEditing(null)}>
								<ArrowLeft size={15} /> {t('common.cancel')}
							</Button>
							<Button onClick={save} disabled={create.isPending || update.isPending}>
								{t('common.save')}
							</Button>
						</div>
					</div>
				) : (
					<div className="flex flex-col gap-3">
						<div className="flex justify-end">
							<Button size="sm" onClick={() => setEditing({ ...EMPTY, cid: customerId ?? 0 })}>
								<Plus size={15} /> {t('customers.list.coupons.add')}
							</Button>
						</div>
						<div className="max-h-[55vh] overflow-auto rounded-md border border-border">
							{isLoading ? (
								<div className="flex flex-col gap-2 p-3">
									{Array.from({ length: 4 }).map((_, i) => (
										<Skeleton key={i} className="h-9 w-full rounded-md" />
									))}
								</div>
							) : !data || data.length === 0 ? (
								<div className="flex flex-col items-center gap-3 py-12 text-center">
									<Ticket size={32} className="text-foreground-disabled" strokeWidth={1.25} />
									<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
								</div>
							) : (
								<table className="w-full border-collapse text-sm">
									<thead className="bg-elevated text-xs text-foreground-muted">
										<tr className="border-b border-border">
											<th className="px-3 py-2 text-left font-medium">{t('customers.list.coupons.code')}</th>
											<th className="px-3 py-2 text-left font-medium">{t('customers.list.coupons.name')}</th>
											<th className="px-3 py-2 text-right font-medium">{t('customers.list.coupons.amount')}</th>
											<th className="px-3 py-2 text-center font-medium">{t('customers.list.coupons.active')}</th>
											<th className="px-2 py-2" style={{ width: 80 }} />
										</tr>
									</thead>
									<tbody>
										{data.map(c => (
											<tr key={c.id} className="border-b border-border/60 hover:bg-muted/50">
												<td className="px-3 py-2 font-mono text-xs text-foreground-muted">{c.cd}</td>
												<td className="px-3 py-2 text-foreground">{c.nm}</td>
												<td className="px-3 py-2 text-right tabular-nums text-foreground-muted">{c.amt ?? '—'}</td>
												<td className="px-3 py-2 text-center">
													{c.active ? <span className="text-success-fg">✓</span> : <span className="text-foreground-disabled">—</span>}
												</td>
												<td className="px-2 py-1 text-center whitespace-nowrap">
													<Button
														variant="ghost"
														size="icon"
														className="h-7 w-7"
														aria-label={t('common.edit')}
														onClick={() => setEditing(c)}>
														<Pencil size={14} />
													</Button>
													<Button
														variant="ghost"
														size="icon"
														className="h-7 w-7 text-danger-fg"
														aria-label={t('common.delete')}
														onClick={() => del(c)}>
														<Trash2 size={14} />
													</Button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							)}
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
